import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Loader2, Workflow, Save, Zap, Bell, CheckSquare, Clock } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkflowsPage = () => {
  const [settings, setSettings] = useState({
    // Auto-task creation
    auto_create_intake_task: true,
    auto_create_followup_task: true,
    followup_days_after_enrollment: 7,
    auto_create_review_task: false,
    review_days_interval: 30,
    
    // Notifications
    notify_on_case_assignment: true,
    notify_on_note_review: true,
    notify_on_task_due: true,
    notify_days_before_due: 1,
    notify_on_enrollment_expiring: true,
    notify_days_before_expiry: 30,
    
    // Auto-closure rules
    auto_close_inactive_cases: false,
    inactive_days_threshold: 90,
    auto_close_requires_approval: true,
    
    // Approval workflows
    expense_approval_threshold: 500,
    expense_approval_roles: ['operations_manager', 'executive'],
    document_approval_enabled: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/workflow-settings`);
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch workflow settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/workflow-settings`, settings);
      toast.success('Workflow settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workflows-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Workflow className="w-6 h-6" />
              Workflows & Automation
            </h1>
            <p className="text-muted-foreground">Configure automatic tasks, notifications, and approval rules</p>
          </div>
        </div>
        <Button onClick={saveSettings} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Auto Task Creation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckSquare className="w-5 h-5" />
              Automatic Task Creation
            </CardTitle>
            <CardDescription>Automatically create tasks based on events</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Intake Task</p>
                <p className="text-sm text-muted-foreground">Create task when participant is enrolled</p>
              </div>
              <Switch
                checked={settings.auto_create_intake_task}
                onCheckedChange={(v) => setSettings({ ...settings, auto_create_intake_task: v })}
              />
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Follow-up Task</p>
                <p className="text-sm text-muted-foreground">Create follow-up task after enrollment</p>
              </div>
              <Switch
                checked={settings.auto_create_followup_task}
                onCheckedChange={(v) => setSettings({ ...settings, auto_create_followup_task: v })}
              />
            </div>

            {settings.auto_create_followup_task && (
              <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                <Label>Days After Enrollment</Label>
                <Input
                  type="number"
                  value={settings.followup_days_after_enrollment}
                  onChange={(e) => setSettings({ ...settings, followup_days_after_enrollment: parseInt(e.target.value) || 7 })}
                />
              </div>
            )}

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Periodic Review Task</p>
                <p className="text-sm text-muted-foreground">Create regular case review tasks</p>
              </div>
              <Switch
                checked={settings.auto_create_review_task}
                onCheckedChange={(v) => setSettings({ ...settings, auto_create_review_task: v })}
              />
            </div>

            {settings.auto_create_review_task && (
              <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                <Label>Review Interval (days)</Label>
                <Input
                  type="number"
                  value={settings.review_days_interval}
                  onChange={(e) => setSettings({ ...settings, review_days_interval: parseInt(e.target.value) || 30 })}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Automatic Notifications
            </CardTitle>
            <CardDescription>Configure when to send notifications</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Case Assignment</p>
                <p className="text-sm text-muted-foreground">Notify when assigned to a case</p>
              </div>
              <Switch
                checked={settings.notify_on_case_assignment}
                onCheckedChange={(v) => setSettings({ ...settings, notify_on_case_assignment: v })}
              />
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Note Review</p>
                <p className="text-sm text-muted-foreground">Notify when note needs review</p>
              </div>
              <Switch
                checked={settings.notify_on_note_review}
                onCheckedChange={(v) => setSettings({ ...settings, notify_on_note_review: v })}
              />
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Task Due Reminder</p>
                <p className="text-sm text-muted-foreground">Notify before task is due</p>
              </div>
              <Switch
                checked={settings.notify_on_task_due}
                onCheckedChange={(v) => setSettings({ ...settings, notify_on_task_due: v })}
              />
            </div>

            {settings.notify_on_task_due && (
              <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                <Label>Days Before Due</Label>
                <Input
                  type="number"
                  value={settings.notify_days_before_due}
                  onChange={(e) => setSettings({ ...settings, notify_days_before_due: parseInt(e.target.value) || 1 })}
                />
              </div>
            )}

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Enrollment Expiring</p>
                <p className="text-sm text-muted-foreground">Notify before enrollment expires</p>
              </div>
              <Switch
                checked={settings.notify_on_enrollment_expiring}
                onCheckedChange={(v) => setSettings({ ...settings, notify_on_enrollment_expiring: v })}
              />
            </div>

            {settings.notify_on_enrollment_expiring && (
              <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                <Label>Days Before Expiry</Label>
                <Input
                  type="number"
                  value={settings.notify_days_before_expiry}
                  onChange={(e) => setSettings({ ...settings, notify_days_before_expiry: parseInt(e.target.value) || 30 })}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Auto-Closure Rules */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Auto-Closure Rules
            </CardTitle>
            <CardDescription>Automatically close inactive cases</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Auto-Close Inactive Cases</p>
                <p className="text-sm text-muted-foreground">Close cases with no activity</p>
              </div>
              <Switch
                checked={settings.auto_close_inactive_cases}
                onCheckedChange={(v) => setSettings({ ...settings, auto_close_inactive_cases: v })}
              />
            </div>

            {settings.auto_close_inactive_cases && (
              <>
                <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                  <Label>Inactive Days Threshold</Label>
                  <Input
                    type="number"
                    value={settings.inactive_days_threshold}
                    onChange={(e) => setSettings({ ...settings, inactive_days_threshold: parseInt(e.target.value) || 90 })}
                  />
                </div>

                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <p className="font-medium">Require Approval</p>
                    <p className="text-sm text-muted-foreground">Manager must approve auto-closure</p>
                  </div>
                  <Switch
                    checked={settings.auto_close_requires_approval}
                    onCheckedChange={(v) => setSettings({ ...settings, auto_close_requires_approval: v })}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Approval Workflows */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Approval Workflows
            </CardTitle>
            <CardDescription>Configure approval requirements</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Expense Approval Threshold ($)</Label>
              <Input
                type="number"
                value={settings.expense_approval_threshold}
                onChange={(e) => setSettings({ ...settings, expense_approval_threshold: parseInt(e.target.value) || 500 })}
              />
              <p className="text-xs text-muted-foreground">Expenses above this amount require approval</p>
            </div>

            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">Document Approval</p>
                <p className="text-sm text-muted-foreground">Require approval for sensitive documents</p>
              </div>
              <Switch
                checked={settings.document_approval_enabled}
                onCheckedChange={(v) => setSettings({ ...settings, document_approval_enabled: v })}
              />
            </div>

            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Approval Roles</p>
              <div className="flex flex-wrap gap-2">
                {['operations_manager', 'executive', 'system_admin'].map((role) => (
                  <Badge 
                    key={role} 
                    variant={settings.expense_approval_roles?.includes(role) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => {
                      const roles = settings.expense_approval_roles || [];
                      if (roles.includes(role)) {
                        setSettings({ ...settings, expense_approval_roles: roles.filter(r => r !== role) });
                      } else {
                        setSettings({ ...settings, expense_approval_roles: [...roles, role] });
                      }
                    }}
                  >
                    {role.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Click to toggle approval authority</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WorkflowsPage;
